import { Star } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import StarSeekerIcon from '../../../assets/svg/star-seeker.svg?react';
import {
  InitialsAvatar,
  PortalCard,
  Skeleton,
  SkeletonGroup,
  SkeletonText,
  StarRating,
} from '../../portal';
import { getReviewerLabel } from '../dashboardReviewFormatting';
import type { ReviewRow } from '../useDashboardReviewsData';
import { formatShortDate } from '../../../utils/stringHelpers';

export interface LatestReviewsData {
  storeListingFound: boolean;
  rating: number | null;
  /** Index 0 = 1 star … index 4 = 5 stars. */
  reviewsByRating: number[];
  reviews: ReviewRow[];
}

interface LatestReviewsCardProps {
  data: LatestReviewsData | null;
  isLoading: boolean;
  error: string | null;
  reviewsPath: string | null;
}

function ReviewItem({
  review,
  reviewsPath,
}: {
  review: ReviewRow;
  reviewsPath: string | null;
}) {
  const label = getReviewerLabel(review);
  return (
    <li className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <InitialsAvatar name={label} size={20} />
        <span className="text-sm font-bold text-white">{label}</span>
        <StarRating rating={review.rating} size={14} />
        <span className="ml-auto whitespace-nowrap text-xs leading-4 text-seeker-400">
          {formatShortDate(review.createdAt)}
        </span>
      </div>
      {review.review ? (
        <p className="line-clamp-2 text-sm text-white">{review.review}</p>
      ) : (
        <p className="text-sm text-seeker-400">No written review.</p>
      )}
      {reviewsPath ? (
        <Link
          to={reviewsPath}
          className="w-fit rounded-sm text-sm text-seeker-500 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          read all...
        </Link>
      ) : null}
    </li>
  );
}

function RatingHero({ data }: { data: LatestReviewsData }) {
  const total = data.reviewsByRating.reduce((sum, count) => sum + count, 0);
  const rows = [5, 4, 3, 2, 1].map((stars) => {
    const count = data.reviewsByRating[stars - 1] ?? 0;
    return { stars, pct: total === 0 ? 0 : (count / total) * 100 };
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
        <div className="flex items-center gap-2 text-white">
          <StarSeekerIcon className="size-9 fill-current" aria-hidden="true" />
          <span className="text-7xl font-medium leading-none tabular-nums">
            {data.rating == null ? '—' : data.rating.toFixed(1)}
          </span>
        </div>
        <span className="pb-2 text-sm tabular-nums text-seeker-400">
          out of 5 • {total.toLocaleString()}{' '}
          {total === 1 ? 'review' : 'reviews'}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={row.stars}
            className="grid grid-cols-[auto_2.25rem_minmax(0,1fr)] items-center gap-2"
          >
            <StarRating
              rating={row.stars}
              size={10}
              label={`${row.stars} stars`}
            />
            <span className="text-xs leading-4 tabular-nums text-seeker-400">
              {Math.round(row.pct)}%
            </span>
            <div className="h-2 overflow-hidden rounded-sm bg-seeker-700">
              <div
                className="h-full rounded-sm bg-white transition-[width] duration-200 ease-out"
                style={{ width: `${row.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Review row skeleton: 20px meta row, two 20px text lines, 20px "read all" line. */
function ReviewItemSkeleton() {
  return (
    <li className="flex flex-col gap-2">
      <div className="flex h-5 items-center gap-2">
        <Skeleton className="size-5" rounded="full" />
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3.5 w-[74px]" />
        <Skeleton className="ml-auto h-3 w-16" />
      </div>
      <SkeletonText lines={2} />
      <span className="flex h-5 items-center">
        <Skeleton className="h-3.5 w-16" />
      </span>
    </li>
  );
}

/** Mirrors the loaded body 1:1: two review rows + the rating hero. */
export function LatestReviewsSkeleton() {
  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
      <ul className="flex flex-col gap-6">
        <ReviewItemSkeleton />
        <ReviewItemSkeleton />
      </ul>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
          <div className="flex items-center gap-2">
            <Skeleton className="size-9" />
            <Skeleton className="h-[72px] w-28" />
          </div>
          <span className="mb-2 flex h-5 items-center">
            <Skeleton className="h-3.5 w-40" />
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {[5, 4, 3, 2, 1].map((stars) => (
            <div
              key={stars}
              className="grid grid-cols-[auto_2.25rem_minmax(0,1fr)] items-center gap-2"
            >
              <Skeleton className="h-2.5 w-[54px]" />
              <span className="flex h-4 items-center">
                <Skeleton className="h-3 w-7" />
              </span>
              <Skeleton className="h-2 rounded-sm" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Card chrome (title row) shared by the loaded card and the page skeleton. */
export function LatestReviewsCardFrame({ children }: { children: ReactNode }) {
  return (
    <PortalCard className="flex flex-col gap-5 p-5 sm:p-7">
      <div className="flex items-center gap-2 text-white">
        <Star className="size-4" aria-hidden="true" />
        <span className="text-sm font-medium">Latest Reviews</span>
      </div>
      {children}
    </PortalCard>
  );
}

export function LatestReviewsCard({
  data,
  isLoading,
  error,
  reviewsPath,
}: LatestReviewsCardProps) {
  let body: ReactNode;
  if (isLoading) {
    body = (
      <SkeletonGroup label="Loading latest reviews">
        <LatestReviewsSkeleton />
      </SkeletonGroup>
    );
  } else if (error) {
    body = <p className="motion-enter text-sm text-rose-400">{error}</p>;
  } else if (!data || !data.storeListingFound) {
    body = (
      <p className="motion-enter text-sm text-seeker-400">
        Ratings appear once your app is live in the dApp Store.
      </p>
    );
  } else {
    const latest = data.reviews.slice(0, 2);
    body = (
      <div className="motion-enter grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
        {latest.length === 0 ? (
          <p className="text-sm text-seeker-400">No reviews yet.</p>
        ) : (
          <ul className="flex flex-col gap-6">
            {latest.map((review) => (
              <ReviewItem
                key={review.id}
                review={review}
                reviewsPath={reviewsPath}
              />
            ))}
          </ul>
        )}
        <RatingHero data={data} />
      </div>
    );
  }

  return <LatestReviewsCardFrame>{body}</LatestReviewsCardFrame>;
}
